import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Building2, Mail, Lock, AlertCircle, LogIn } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../ui/Button';
import Input from '../ui/Input';

interface AdminLoginFormProps {
  onSuccess?: () => void;
}

const AdminLoginForm: React.FC<AdminLoginFormProps> = ({ onSuccess }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { adminLogin } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const adminUser = await adminLogin(formData.email, formData.password);
      onSuccess?.();
      // Check if this is a master user and redirect accordingly
      const isMasterUser = (adminUser as any)?.role === 'master' || (adminUser as any)?.isMaster === true;
      if (isMasterUser) {
        navigate('/master');
      } else {
        navigate('/admin');
      }
    } catch (err: any) {
      const errorMessage = err?.message || t('auth.adminLogin.invalidCredentials', 'Invalid admin credentials');
      setError(errorMessage);
      console.error('Login error:', err);
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-900 via-blue-900 to-indigo-900 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-4 glow">
            <Building2 size={32} className="text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            {t('auth.adminLogin.title', 'Admin Portal')}
          </h1>
          <p className="text-white/70">
            {t('auth.adminLogin.subtitle', 'Sign in to your company dashboard')}
          </p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="glass-card p-6 rounded-3xl">
            {error && (
              <div className="flex items-center space-x-2 text-red-400 bg-red-400/10 border border-red-400/20 rounded-2xl p-4 mb-6">
                <AlertCircle size={20} />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <div className="space-y-4">
              <Input
                type="email"
                name="email"
                placeholder={t('auth.adminLogin.emailPlaceholder', 'Company Email')}
                value={formData.email}
                onChange={handleInputChange}
                required
                disabled={isLoading}
                className="bg-white/10 border-white/20 text-white placeholder-white/50"
                icon={<Mail size={20} className="text-white/50" />}
              />

              <Input
                type="password"
                name="password"
                placeholder={t('auth.adminLogin.passwordPlaceholder', 'Password')}
                value={formData.password}
                onChange={handleInputChange}
                required
                disabled={isLoading}
                className="bg-white/10 border-white/20 text-white placeholder-white/50"
                icon={<Lock size={20} className="text-white/50" />}
              />
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full mt-6 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-semibold py-3 rounded-2xl glow hover:scale-105 transition-all duration-300"
            >
              {isLoading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                  <span>{t('auth.adminLogin.signingIn', 'Signing In...')}</span>
                </div>
              ) : (
                <div className="flex items-center justify-center space-x-2">
                  <LogIn size={20} />
                  <span>{t('auth.adminLogin.signIn', 'Sign In')}</span>
                </div>
              )}
            </Button>
          </div>
        </form>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-white/60 text-sm">
            {t('auth.adminLogin.needAccount', 'Need an admin account?')}{' '}
            <button 
              onClick={() => navigate('/master')}
              className="text-primary-400 hover:text-primary-300 font-medium"
            >
              {t('auth.adminLogin.contactMaster', 'Contact Master Admin')}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginForm; 